import type { ResolvedProject } from "@designs-by-maryam/types";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@designs-by-maryam/ui/components/tabs";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { type Project, ProjectCard } from "@/components/projects/project-card";
import { orpc } from "@/utils/orpc";

/**
 * Category configuration for filter tabs
 */
const CATEGORIES: { value: string; label: string }[] = [
	{ value: "all", label: "All" },
	{ value: "residential", label: "Residential" },
	{ value: "commercial", label: "Commercial" },
	{ value: "hospitality", label: "Hospitality" },
];

type FeaturedProjectsProps = {
	className?: string;
	/**
	 * Callback when a project card is clicked
	 * Used to open the project detail modal
	 */
	onProjectClick?: (projectId: string) => void;
	/**
	 * Pre-resolved projects from Puck resolveData
	 * If provided and non-empty, these will be displayed instead of auto-fetching
	 */
	resolvedProjects?: ResolvedProject[];
};

/**
 * Loading skeleton component for the featured projects grid
 */
function FeaturedProjectsSkeleton() {
	return (
		<div className="space-y-8">
			{/* Tabs skeleton */}
			<div className="flex justify-center">
				<Skeleton className="h-10 w-full max-w-md rounded-lg" />
			</div>
			{/* Grid skeleton */}
			<div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{Array.from({ length: 3 }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: Static skeleton items never reorder
					<div className="space-y-4" key={`skeleton-${i}`}>
						<Skeleton className="aspect-[4/3] w-full rounded-lg" />
						<div className="space-y-2 p-4">
							<Skeleton className="h-5 w-3/4" />
							<Skeleton className="h-4 w-1/2" />
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

/**
 * Empty state component for when no projects match the filter
 */
function EmptyState({ category }: { category: string }) {
	const categoryLabel = category === "all" ? "featured" : category;
	return (
		// biome-ignore lint/a11y/useSemanticElements: role="status" is correct for live region announcements, not <output>
		<div
			aria-live="polite"
			className="flex flex-col items-center justify-center py-12 text-center"
			role="status"
		>
			<p className="text-muted-foreground">
				No {categoryLabel} projects available yet.
			</p>
		</div>
	);
}

/**
 * FeaturedProjects Component
 *
 * Displays a filterable grid of featured projects on the homepage.
 * Fetches all featured projects once and handles filtering client-side
 * for instant tab switching without additional API calls.
 *
 * Features:
 * - Category filter tabs with ARIA accessibility
 * - Responsive grid (3 cols desktop, 2 cols tablet, 1 col mobile)
 * - Client-side filtering for instant response
 * - Loading skeleton and error states
 * - TanStack Query for caching (5-minute stale time)
 */
export function FeaturedProjects({
	className,
	onProjectClick,
	resolvedProjects,
}: FeaturedProjectsProps) {
	const [activeCategory, setActiveCategory] = useState<string>("all");

	// Determine if we should use pre-resolved projects or auto-fetch
	const hasResolvedProjects = resolvedProjects && resolvedProjects.length > 0;

	// Fetch featured projects with TanStack Query (only if no resolved projects)
	const { data, isLoading, error } = useQuery({
		...orpc.getFeaturedProjects.queryOptions(),
		enabled: !hasResolvedProjects, // Skip API call if we have resolved projects
	});

	// Extract projects from response - cast the response type for TypeScript
	const responseData = data as
		| {
				success: true;
				data: Project[];
				pagination: { total: number; hasMore: boolean };
		  }
		| undefined;

	// Use resolved projects if available, otherwise use fetched projects
	const projects: Project[] = hasResolvedProjects
		? resolvedProjects.map((p) => ({
				id: p.id,
				title: p.title,
				short_description: null, // Not needed for grid display
				category: p.category,
				location: null,
				year: p.year,
				images: p.images,
				is_featured: true,
				status: "published" as const,
			}))
		: responseData?.data || [];

	// Maximum projects to display on homepage (limit for performance)
	const MAX_PROJECTS = 3;

	// Client-side filtering with memoization for performance
	const filteredProjects = useMemo(() => {
		const filtered =
			activeCategory === "all"
				? projects
				: projects.filter((project) => project.category === activeCategory);
		// Limit to MAX_PROJECTS for homepage display
		return filtered.slice(0, MAX_PROJECTS);
	}, [projects, activeCategory]);

	// Loading state (only when fetching, not when using resolved projects)
	if (isLoading && !hasResolvedProjects) {
		return (
			<section
				aria-label="Featured Projects"
				className={cn("container mx-auto px-4 py-12 md:py-16", className)}
				data-testid="featured-projects-section"
			>
				<div className="mb-10 text-center">
					<h2 className="font-bold text-3xl text-foreground tracking-tight sm:text-4xl">
						Featured Projects
					</h2>
					<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
						Explore our award-winning designs across residential, commercial,
						and hospitality sectors.
					</p>
				</div>
				<FeaturedProjectsSkeleton />
			</section>
		);
	}

	// Error state (only when fetching, not when using resolved projects)
	if ((error || !responseData) && !hasResolvedProjects) {
		return (
			<section
				aria-label="Featured Projects"
				className={cn("container mx-auto px-4 py-12 md:py-16", className)}
				data-testid="featured-projects-section"
			>
				<div className="text-center text-muted-foreground">
					<p>Unable to load featured projects. Please try again later.</p>
				</div>
			</section>
		);
	}

	return (
		<section
			aria-labelledby="featured-projects-heading"
			className={cn(
				"relative container mx-auto overflow-hidden px-4 py-16 md:py-24",
				className,
			)}
			data-testid="featured-projects-section"
		>
			{/* Decorative corner shapes */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-0 right-0 h-64 w-64 translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-3xl"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-0 left-0 h-64 w-64 -translate-x-1/2 translate-y-1/2 rounded-full bg-purple-500/3 blur-3xl"
			/>
			{/* Section Header */}
			<div className="relative mb-10 text-center">
				<h2
					className="bg-gradient-to-br from-foreground via-foreground to-foreground/70 bg-clip-text font-bold text-3xl text-transparent tracking-tight sm:text-4xl md:text-5xl"
					id="featured-projects-heading"
					style={{ letterSpacing: "-0.02em" }}
				>
					Featured Projects
				</h2>
				<p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
					Explore our award-winning designs across residential, commercial, and
					hospitality sectors.
				</p>
			</div>

			{/* Category Filter Tabs */}
			<Tabs
				className="w-full"
				defaultValue="all"
				onValueChange={setActiveCategory}
				value={activeCategory}
			>
				<div className="mb-8 flex justify-center">
					<TabsList
						aria-label="Filter projects by category"
						className="h-auto rounded-2xl border-2 border-primary/20 bg-muted/40 p-2 shadow-lg backdrop-blur-sm"
					>
						{CATEGORIES.map((cat) => (
							<TabsTrigger
								className={cn(
									"rounded-xl px-5 py-2.5 font-medium text-sm transition-all",
									// Active tab styling - upgraded with ring and shadow
									"data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-ring/20",
								)}
								data-testid={`tab-${cat.value}`}
								key={cat.value}
								value={cat.value}
							>
								{cat.label}
							</TabsTrigger>
						))}
					</TabsList>
				</div>

				{/* Tab Content Panels */}
				{CATEGORIES.map((cat) => (
					<TabsContent
						className="mt-0 focus-visible:outline-none focus-visible:ring-0"
						data-testid={`tab-content-${cat.value}`}
						key={cat.value}
						value={cat.value}
					>
						{/* Only render when this tab is active to avoid layout issues */}
						{activeCategory === cat.value &&
							(filteredProjects.length > 0 ? (
								<ul
									aria-label={`${cat.label} projects`}
									className="grid list-none grid-cols-1 gap-6 md:grid-cols-3"
								>
									{filteredProjects.map((project) => (
										<li key={project.id}>
											<ProjectCard
												onClick={() => onProjectClick?.(project.id)}
												project={project}
											/>
										</li>
									))}
								</ul>
							) : (
								<EmptyState category={cat.value} />
							))}
					</TabsContent>
				))}
			</Tabs>

			{/* Results count for screen readers */}
			<div aria-atomic="true" aria-live="polite" className="sr-only">
				{filteredProjects.length}{" "}
				{activeCategory === "all" ? "" : activeCategory} projects displayed
			</div>
		</section>
	);
}

export default FeaturedProjects;
